import styled from 'styled-components';
import { readableColor } from 'polished';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import readableText from 'Styles/readable-text';
import { getStyle } from 'Styles/cardFontStyles';
import { BORDER_RADIUS } from '../Card/Card.styled';
import { Button as StyledButton } from '../Button/Button.styled';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';

const TEXT_SHADOW = 'text-shadow: 0 0 8px rgba(16, 14, 32, 0.08);';
const LARGE_CTA_PADDING = '13px 16px';
const MEDIUM_CTA_PADDING = '10px 16px';
const SMALL_CTA_PADDING = '6px 16px';

export const Subtitle = styled.div`
  ${({ $responsiveStyleSize = 'regular' }) =>
    getStyle($responsiveStyleSize, 'text')}
  a {
    color: inherit;
    text-decoration: unset;
  }

  h2 {
    ${({ $responsiveStyleSize = 'regular' }) =>
      getStyle($responsiveStyleSize, 'title')}
    margin-bottom: 4px;
  }

  color: ${({ $textColor }) =>
    $textColor === 'LIGHT'
      ? convertColorToThemeHexCode('white')
      : convertColorToThemeHexCode('black')};
  ${TEXT_SHADOW}
`;

export const Title = styled.h2`
  ${({ $responsiveStyleSize = 'regular' }) =>
    getStyle($responsiveStyleSize, 'title')}
  color: ${({ $textColor }) =>
    $textColor === 'LIGHT'
      ? convertColorToThemeHexCode('white')
      : convertColorToThemeHexCode('black')};
  margin-bottom: 4px;
  ${TEXT_SHADOW}
  white-space: nowrap;
`;

export const Cta = styled(StyledButton)`
  margin: 4px 8px 8px 0;
  ${({ $textColor }) => {
    if ($textColor) {
      const backgroundColor = readableColor(
        $textColor === 'DARK'
          ? convertColorToThemeHexCode('white')
          : convertColorToThemeHexCode('black')
      );
      return readableText(backgroundColor);
    }
  }};
  /* Only allow this child in the Copy component to be interactive */
  pointer-events: auto;
  overflow-wrap: anywhere;

  /* When text wraps, specifying a top/bottom padding will keeps wrapping text away from the edges. */
  ${({ size = 'small' }) => {
    if (size === 'large') return `padding: ${LARGE_CTA_PADDING};`;
    if (size === 'medium') return `padding: ${MEDIUM_CTA_PADDING};`;
    if (size === 'small') return `padding: ${SMALL_CTA_PADDING};`;
  }}

  &:last-of-type {
    margin-right: 0;
  }
`;

export const Copy = styled.div`
  display: flex;
  flex-direction: column;
  ${({ $alignment }) => {
    if ($alignment === 'center') {
      return 'align-items: center;';
    }

    return 'align-items: baseline;';
  }}
  ${({ $verticalAlignment = 'Top' }) => {
    if ($verticalAlignment === 'Center') {
      return 'justify-content: center;';
    }

    if ($verticalAlignment === 'Bottom') {
      return 'justify-content: flex-end;';
    }

    return 'justify-content: flex-start;';
  }}
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${({ $responsiveStyleSize = 'regular' }) =>
    getStyle($responsiveStyleSize, 'padding')};
  /* Copy will otherwise block the 'a' tag that wraps the image, so it is made non-interactive */
  pointer-events: none;
  ${TEXT_SHADOW}

  & ${Cta} {
    margin-top: 8px;
  }

  & ${Title} + ${Cta} {
    margin-top: 4px;
  }
`;

export const Ctas = styled.div`
  align-items: center;
  border-radius: ${BORDER_RADIUS};
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
  padding: ${({ $responsiveStyleSize }) =>
    $responsiveStyleSize === 'mega' ? '24px' : '12px'};

  ${Cta} {
    margin: 8px 0 0 0;
    max-width: 250px;
    width: 100%;
    text-align: center;
  }

  ${Cta}:first-of-type {
    margin-top: 0;
  }
`;

export const CtasBackdrop = styled.div`
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  padding: ${({ $responsiveStyleSize }) =>
    $responsiveStyleSize === 'mega' ? '24px' : '12px'};
  background-color: var(--hover);
  opacity: 0.5;
`;

export const CtasContainer = styled.div`
  height: calc(100% + 2px);
  position: absolute;
  left: 0;
  top: -2px;
  width: 100%;
`;

export const Image = styled(ResponsiveImage)`
  width: 100%;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'auto')};
  overflow: hidden;
  border-radius: 6px;
`;

export const ImageCard = styled.div`
  position: relative;
  grid-column: ${({ $gridSpan }) => `span ${$gridSpan}`};
  text-align: ${({ $alignment }) =>
    $alignment === 'left' ? 'left' : 'center'};

  img {
    border-radius: ${BORDER_RADIUS};
  }
`;

export const Link = styled.a`
  text-decoration: unset;
  color: unset;
`;
